import { useRef, type ComponentProps } from "react";
import styled from "styled-components";

import { Text } from "../Text";
import { VideoSurface, useVideoTimeline, type PlaybackTransport, type VideoStatus } from "../Video";
import { ZoomPanWrapper } from "../ZoomPan";
import { controlTransition } from "../foundations/controlStyles";
import { CapturedFrameViewport, type CoordinatePointPreview } from "./CapturedFrameViewport";
import { MediaResultPlayer } from "./MediaResultPlayer";
import {
  TrackingPreviewStatusIndicator,
  TrackingPreviewViewport,
  type TrackingPreviewPlayback,
} from "./TrackingPreview";
import {
  ViewportOverlayActions,
  annotationStageProps,
  annotationStageFitCss,
} from "./AnnotationViewportPrimitives";
import {
  useAnnotationRasterStage,
  useVideoRaster,
  type RasterReporter,
} from "./annotationRasterStage";
import { annotationStudioText } from "./messages";
import type {
  AnnotationFrameDraft,
  AnnotationMediaResultBinding,
  AnnotationMediaSource,
  AnnotationPointKind,
  AnnotationStream,
  AnnotationStudioController,
  AnnotationStudioLocale,
  AnnotationTarget,
  AnnotationTrackingPreviewState,
  CapturedFrameRenderResource,
} from "./types";

type CapturedFrameViewportTargets = ComponentProps<typeof CapturedFrameViewport>["targets"];
export type StudioMediaContext =
  | { readonly kind: "video" }
  | { readonly kind: "captured-frame"; readonly frameId: string };

export interface StudioViewportProps {
  readonly controller: AnnotationStudioController;
  readonly locale: AnnotationStudioLocale;
  readonly mediaContext: StudioMediaContext;
  readonly browseMediaKey: string;
  readonly stream?: AnnotationStream;
  readonly media?: AnnotationMediaSource | null;
  readonly capturedFrameMedia?: AnnotationMediaSource | null;
  readonly trackingStream?: AnnotationStream;
  readonly trackingMedia?: AnnotationMediaSource | null;
  readonly frame?: AnnotationFrameDraft;
  readonly capturedFrameResource: CapturedFrameRenderResource | null;
  readonly mediaResult?: AnnotationMediaResultBinding | null;
  readonly capturedFrameEditable: boolean;
  readonly targets: readonly AnnotationTarget[];
  readonly frameTargets: CapturedFrameViewportTargets;
  /** 브라우즈 타임라인 store — 시간은 여기서만 국소 구독한다(트리 전체 리렌더 금지). */
  readonly browseTransport: PlaybackTransport;
  readonly trackingPreview: AnnotationTrackingPreviewState;
  readonly trackingPlayback: TrackingPreviewPlayback;
  readonly coordinateGuideVisible: boolean;
  readonly coordinatePreview: CoordinatePointPreview | null;
  readonly onTimelineDurationChange: (durationSeconds: number) => void;
  readonly onTimelineCurrentTimeChange: (currentTimeSeconds: number) => void;
  readonly onTimelineStatusChange: (status: VideoStatus) => void;
  readonly onTimelineEnded: () => void;
  readonly onPoint: (kind: AnnotationPointKind, x: number, y: number) => void;
  readonly onRemovePoint: (targetId: string, pointId: string) => void;
}

export const ViewportStage = styled.div<{ $flush: boolean }>`
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 16rem;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: ${({ $flush }) => ($flush ? "0" : "0.65rem")};
  background: ${({ theme }) => theme.color.bg.surface};
  container-type: size;

  @media (max-height: 760px) {
    min-height: 13rem;
    padding: ${({ $flush }) => ($flush ? "0" : "0.45rem")};
  }
`;

const ViewportLayer = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $active }) => ($active ? 1 : 0)};
  display: flex;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  ${controlTransition("opacity")}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FrameCaptureZoomViewport = styled(ZoomPanWrapper)`
  ${annotationStageFitCss}
`;

const MediaVideoSurface = styled(VideoSurface)`
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  & > div {
    height: 100%;
    border: 0;
  }
`;

/** 시간-빈도 구독을 브라우즈 비디오 한 층에 가둔다 — 상위 viewport는 시간에 리렌더하지 않는다. */
function BrowseVideoSurface({
  transport,
  media,
  streamLabel,
  active,
  ariaLabel,
  onDurationChange,
  onCurrentTimeChange,
  onStatusChange,
  onEnded,
  onRasterChange,
}: {
  transport: PlaybackTransport;
  media: AnnotationMediaSource;
  streamLabel: string;
  active: boolean;
  ariaLabel: string;
  onDurationChange: (durationSeconds: number) => void;
  onCurrentTimeChange: (currentTimeSeconds: number) => void;
  onStatusChange: (status: VideoStatus) => void;
  onEnded: () => void;
  onRasterChange: RasterReporter;
}) {
  const playing = useVideoTimeline(transport, (snapshot) => snapshot.playing);
  const currentTimeSeconds = useVideoTimeline(transport, (snapshot) => snapshot.currentTimeSeconds);
  const playbackRate = useVideoTimeline(transport, (snapshot) => snapshot.playbackRate);
  const setVideoRef = useVideoRaster(onRasterChange);
  return (
    <MediaVideoSurface
      ref={setVideoRef}
      src={media.src}
      poster={media.poster}
      crossOrigin={media.crossOrigin}
      controls={false}
      label={streamLabel}
      showLabel={false}
      playing={active && playing}
      currentTimeSeconds={currentTimeSeconds}
      playbackRate={playbackRate}
      timeUpdateIntervalMs={100}
      onDurationChange={onDurationChange}
      onCurrentTimeChange={onCurrentTimeChange}
      onStatusChange={onStatusChange}
      onEnded={onEnded}
      aria-label={ariaLabel}
    />
  );
}

/**
 * 브라우즈 영상의 줌 viewport. 비율을 표시 중인 영상의 실제 크기에서 구하므로 그 영상에서 캡처한 프레임의
 * viewport와 같은 사각형을 쓴다.
 */
function BrowseVideoViewport({
  locale,
  media,
  ...videoProps
}: Omit<ComponentProps<typeof BrowseVideoSurface>, "media" | "onRasterChange"> & {
  locale: AnnotationStudioLocale;
  media: AnnotationMediaSource;
}) {
  const stage = useAnnotationRasterStage({
    hasRaster: Boolean(media.src),
    declaredAspectRatio: media.aspectRatio,
  });
  return (
    <FrameCaptureZoomViewport
      minZoom={1}
      bordered={false}
      showZoomControls
      resetLabel={annotationStudioText("resetView", locale)}
      zoomInLabel={annotationStudioText("zoomIn", locale)}
      zoomOutLabel={annotationStudioText("zoomOut", locale)}
      data-frame-capture-zoom-viewport=""
      {...annotationStageProps(stage)}
    >
      <BrowseVideoSurface {...videoProps} media={media} onRasterChange={stage.reportRaster} />
    </FrameCaptureZoomViewport>
  );
}

export function StudioViewport({
  controller,
  locale,
  mediaContext,
  browseMediaKey,
  stream,
  media,
  capturedFrameMedia,
  trackingStream,
  trackingMedia,
  frame,
  capturedFrameResource,
  mediaResult,
  capturedFrameEditable,
  targets,
  frameTargets,
  browseTransport,
  trackingPreview,
  trackingPlayback,
  coordinateGuideVisible,
  coordinatePreview,
  onTimelineDurationChange,
  onTimelineCurrentTimeChange,
  onTimelineStatusChange,
  onTimelineEnded,
  onPoint,
  onRemovePoint,
}: StudioViewportProps) {
  const resultActive = mediaResult !== null && mediaResult !== undefined;
  const trackingActive = !resultActive && trackingPlayback.active && trackingStream !== undefined;
  const trackingFrameReady =
    trackingPreview.latestFrame !== null || (trackingPreview.frames?.length ?? 0) > 0;
  const trackingPending = trackingActive && !trackingFrameReady;
  const trackingResultActive = trackingActive && trackingFrameReady;
  const browsePresentationRef = useRef({ key: browseMediaKey, stream, media });
  if (
    mediaContext.kind === "video" &&
    (!trackingActive || browsePresentationRef.current.key !== browseMediaKey)
  ) {
    browsePresentationRef.current = { key: browseMediaKey, stream, media };
  }
  const browsePresentation = browsePresentationRef.current;
  const videoActive =
    mediaContext.kind === "video" &&
    browsePresentation.stream !== undefined &&
    Boolean(browsePresentation.media) &&
    !resultActive &&
    !trackingResultActive;
  const capturedFrameContextActive =
    mediaContext.kind === "captured-frame" && frame?.frameId === mediaContext.frameId;
  const capturedFrameActive = capturedFrameContextActive && !trackingResultActive && !resultActive;
  const hasActiveContent = videoActive || trackingActive || capturedFrameActive || resultActive;

  return (
    <ViewportStage
      $flush={Boolean(browsePresentation.media || frame || trackingStream || mediaResult)}
      data-annotation-viewport-stage=""
    >
      {browsePresentation.stream && browsePresentation.media ? (
        <ViewportLayer
          $active={videoActive}
          aria-hidden={!videoActive}
          inert={!videoActive}
          data-media-context-layer="video"
          data-active={videoActive ? "true" : "false"}
        >
          <BrowseVideoViewport
            key={browsePresentation.key}
            locale={locale}
            transport={browseTransport}
            media={browsePresentation.media}
            streamLabel={browsePresentation.stream.label}
            active={videoActive}
            ariaLabel={`${browsePresentation.stream.label} ${annotationStudioText("liveVideo", locale)}`}
            onDurationChange={onTimelineDurationChange}
            onCurrentTimeChange={onTimelineCurrentTimeChange}
            onStatusChange={onTimelineStatusChange}
            onEnded={onTimelineEnded}
          />
        </ViewportLayer>
      ) : null}

      {frame ? (
        <ViewportLayer
          $active={capturedFrameActive}
          aria-hidden={!capturedFrameActive}
          inert={!capturedFrameActive}
          data-media-context-layer="captured-frame"
          data-active={capturedFrameActive ? "true" : "false"}
        >
          <CapturedFrameViewport
            frame={frame}
            frameResource={capturedFrameResource}
            targets={frameTargets}
            mediaSrc={capturedFrameMedia?.src}
            poster={capturedFrameMedia?.poster}
            crossOrigin={capturedFrameMedia?.crossOrigin}
            label={annotationStudioText("capturedFrame", locale)}
            coordinateGuideVisible={coordinateGuideVisible}
            coordinatePreview={coordinatePreview}
            editable={capturedFrameEditable}
            interactionActive={capturedFrameActive}
            removePointLabel={annotationStudioText("removePoint", locale)}
            resetViewLabel={annotationStudioText("resetView", locale)}
            zoomInLabel={annotationStudioText("zoomIn", locale)}
            zoomOutLabel={annotationStudioText("zoomOut", locale)}
            onPoint={onPoint}
            onRemovePoint={onRemovePoint}
          />
        </ViewportLayer>
      ) : null}

      {mediaResult ? (
        <ViewportLayer
          $active
          aria-hidden={false}
          data-media-context-layer="media-result"
          data-active="true"
        >
          <MediaResultPlayer binding={mediaResult} targets={targets} locale={locale} />
        </ViewportLayer>
      ) : null}

      {trackingPending ? (
        <ViewportLayer
          $active
          role="img"
          aria-label={annotationStudioText("trackingPreviewViewport", locale)}
          data-media-context-layer="tracking-preview"
          data-tracking-preview-pending=""
          data-active="true"
        >
          <ViewportOverlayActions>
            <TrackingPreviewStatusIndicator
              statusLabel={annotationStudioText("trackingRunning", locale)}
              framesSeen={trackingPreview.framesSeen}
            />
          </ViewportOverlayActions>
        </ViewportLayer>
      ) : null}

      {trackingResultActive && trackingStream ? (
        <ViewportLayer
          $active
          aria-hidden={false}
          data-media-context-layer="tracking-preview"
          data-active="true"
        >
          <TrackingPreviewViewport
            controller={controller}
            preview={trackingPreview}
            playback={trackingPlayback}
            targets={targets}
            mediaSrc={trackingMedia?.src}
            poster={trackingMedia?.poster}
            crossOrigin={trackingMedia?.crossOrigin}
            declaredAspectRatio={trackingMedia?.aspectRatio}
            fallbackTimestampSeconds={frame?.timestampSeconds ?? 0}
            label={annotationStudioText("trackingPreviewViewport", locale)}
            statusLabel={annotationStudioText(
              trackingPreview.status === "complete"
                ? "trackingComplete"
                : trackingPreview.status === "error"
                  ? "trackingFailed"
                  : "trackingRunning",
              locale,
            )}
          />
        </ViewportLayer>
      ) : null}

      {!hasActiveContent ? <Text>{annotationStudioText("emptyWorkspace", locale)}</Text> : null}
    </ViewportStage>
  );
}
